import { deviceSession } from '@/privacy/deviceStorage';
/**
 * I-2102 (under I-2092): a sign-out with no dialog (a revoked device, the Terms screen, mobile pairing, a
 * lost credential) that removed the person's data because the device could not tell whether their
 * organisation requires it says so on the screen it lands on, so the person knows why it is gone.
 * Kept for the tab only (`sessionStorage`) and read once by the sign-in page.
 */
export const SIGN_OUT_DATA_NOTICE_KEY = 'featit:signOutDataNotice';

export function noteDataRemovedUnchecked(): void {
  try {
    deviceSession.setItem(SIGN_OUT_DATA_NOTICE_KEY, 'unchecked');
  } catch {
    // A tab that will not keep it shows no notice; the data is gone all the same.
  }
}

/**
 * I-2229: the person signed out on purpose, to continue with an invitation as another account. The
 * sign-in page says so instead of the unchecked removal's sentence, and says the removal (if there was
 * one) as what it is: a precaution, not a loss. Written after `logout()`, which notes the removal
 * synchronously, so it reads what that sign-out did.
 */
export function noteSignedOutToSwitch(): void {
  try {
    const removed = deviceSession.getItem(SIGN_OUT_DATA_NOTICE_KEY) !== null;
    deviceSession.setItem(SIGN_OUT_DATA_NOTICE_KEY, removed ? 'switched-removed' : 'switched');
  } catch {
    // A tab that will not keep it shows no notice.
  }
}

export type SignOutNoticeType = 'unchecked' | 'switched' | 'switched-removed';

/** Which notice is waiting, if any. A pure read, so a render run twice reads it twice. */
export function signOutNoticeWaiting(): SignOutNoticeType | null {
  try {
    const value = deviceSession.getItem(SIGN_OUT_DATA_NOTICE_KEY);
    if (value === null) return null;
    return value === 'switched' || value === 'switched-removed' ? value : 'unchecked';
  } catch {
    return null;
  }
}

/** Whether a notice is waiting. */
export function dataRemovedNoticeWaiting(): boolean {
  return signOutNoticeWaiting() !== null;
}

/** Forgets it once the page has shown it: it is shown once. */
export function forgetDataRemovedNotice(): void {
  try {
    deviceSession.removeItem(SIGN_OUT_DATA_NOTICE_KEY);
  } catch {
    // Nothing to forget.
  }
}
